"use client";

import { useLocale, useTranslations } from "next-intl";
import { useAuth } from "@auth/client-sdk-react";
import {
  Footer as DesignSystemFooter,
  type TFooterProps as TDesignSystemFooterProps,
} from "@/ui/components/navigation/Footer";
import { cn } from "@/ui/lib";
import { isGermanLocale, resolveLocale } from "libs/i18n/src/lib/locales";
import {
  getFooterPrimaryNavigation,
  getFooterSecondaryNavigation,
  getFooterSignedInPrimaryNavigation,
  getFooterSocialNavigation,
} from "apps/website/utils/footer/footer-navigation";
import {
  applySignUpFunnelLinks,
} from "apps/website/utils/footer/sign-up-funnel-links";

import { CountryToggle } from "./CountryToggle";
import { useFooterNewsletterSignup } from "./useFooterNewsletterSignup";

type TNavigationProps = "primaryNavigation" | "secondaryNavigation" | "socialNavigation";
type TManagedProps = "newsletter" | "countryToggle" | "isSignedIn";

export type TFooterProps = Omit<TDesignSystemFooterProps, TNavigationProps | TManagedProps>
  & Partial<Pick<TDesignSystemFooterProps, TNavigationProps>>
  & {
    /** Replaces the plain sign-up link on funnel pages, see getSignUpFunnelHref */
    signUpHref?: string | null;
  };

export default function Footer({
  signUpHref,
  primaryNavigation,
  secondaryNavigation,
  socialNavigation,
  className,
  ...props
}: TFooterProps) {
  const locale = resolveLocale(useLocale());
  const t = useTranslations("Components.Footer.Navigation");
  const { loggedIn } = useAuth();
  const newsletter = useFooterNewsletterSignup();
  const isSignedIn = loggedIn === true;
  const basePrimaryNavigation = primaryNavigation
    ?? (isSignedIn
      ? getFooterSignedInPrimaryNavigation(t, locale)
      : getFooterPrimaryNavigation(t, locale));
  const resolvedPrimaryNavigation = applySignUpFunnelLinks(basePrimaryNavigation, signUpHref);

  return (
    <DesignSystemFooter
      { ...props }
      isSignedIn={ isSignedIn }
      primaryNavigation={ resolvedPrimaryNavigation }
      secondaryNavigation={ secondaryNavigation ?? getFooterSecondaryNavigation(t, locale) }
      socialNavigation={ socialNavigation ?? getFooterSocialNavigation(t) }
      newsletter={ isGermanLocale(locale) ? null : newsletter }
      countryToggle={ <CountryToggle /> }
      className={ cn("pb-24", className) }
    />
  );
}
